import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { FormattedMessage } from '../../util/reactIntl';
import { Page, Footer, IconSpinner, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { getImagesLink } from '../../util/dataExtractors';
import { convertMarkdownToReact } from '../../util/markdownHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import SectionRandomListings from '../BlogPage/SectionRandomListings';
import { isArrayLength } from '../../util/genericHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';

import css from './SingleBlogPage.module.css';
import FooterContainer from '../FooterContainer/FooterContainer';
import { queryListingsByKeyword } from './SingleBlogPage.duck';

const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

function extractUrls(description) {
  if (!description) return null;

  const urlPattern = /https:\/\/you-and-a-view\.com\/wohnmobilstellplatz\/([^\s/)\]]+)/g;
  let matches = [];
  let match;

  // Use a loop to find all matches
  while ((match = urlPattern.exec(description)) !== null) {
    matches.push(match[1]); // Capture the specific part of the URL
  }

  return matches.length > 0 ? matches : [];
}

export const SingleBlogPageComponent = props => {
  const { scrollingDisabled, blogData, pageListings, viewport } = props;
  const config = useConfiguration();
  const dispatch = useDispatch();

  const blogImages = blogData?.attributes?.images?.data;
  const title = blogData?.attributes?.title;
  const description = blogData?.attributes?.description;
  const imagesLink = getImagesLink(blogImages, baseUrl);

  const [reformedDescription, setRefactoredDescription] = useState(description);

  const fetchListingsById = async keyword => {
    return await dispatch(queryListingsByKeyword(keyword));
  };

  useEffect(() => {
    if (description) {
      const descriptions = extractUrls(description);
      let updatedDescription = description;

      const updateDescriptionWithIds = async () => {
        for (let keyword of descriptions) {
          const id = await fetchListingsById(keyword);
          if (id) {
            const fullUrl = `https://you-and-a-view.com/wohnmobilstellplatz/${keyword}/${id}`;

            // Replace the URL within Markdown syntax
            const markdownUrlPattern = new RegExp(
              `\\[([^\\]]+)\\]\\(https:\/\/you-and-a-view\\.com\\/wohnmobilstellplatz\\/${keyword}\\)`,
              'g'
            );
            updatedDescription = updatedDescription.replace(markdownUrlPattern, (match, p1) => {
              const newMarkdownLink = `[${p1}](${fullUrl})`;
              return newMarkdownLink;
            });
          }
        }

        // Once all replacements are done, update the state
        setRefactoredDescription(updatedDescription);
      };

      updateDescriptionWithIds();
    }
  }, [description]);


  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={description}
      title={title}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: description,
        name: title,
        image: [facebookImage],
      }}
    >
      <LayoutSingleColumn
        showMobileNavBar
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer showMobileNavBar />}
      >
        <div className={css.pageContent}>
          <div className={css.blogsContainer}>
            <div className={css.blogsContent}>
              {!blogData ? (
                <div className={css.loader}>
                  <IconSpinner />
                </div>
              ) : null}
              {reformedDescription ? convertMarkdownToReact(reformedDescription) : description}
            </div>
          </div>
        </div>
        <div className={css.locationSection}>
          <h3>
            {' '}
            <FormattedMessage id="SingleBlogPage.exploreOurListings" />
          </h3>
          <SectionRandomListings
            viewport={viewport}
            listings={isArrayLength(pageListings) ? pageListings.slice(0, 10) : []}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

SingleBlogPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { searchInProgress, blogData, searchBlogsError } = state.SingleBlogPage;
  const { currentUser } = state.user;
  const { currentPageResultIds } = state.SearchPage;
  const pageListings = getListingsById(state, currentPageResultIds);

  const onlyBookableListings = isArrayLength(pageListings)
    ? pageListings.filter(l => !l?.attributes?.publicData?.isInfoOnly)
    : [];

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    searchInProgress,
    blogData,
    searchBlogsError,
    pageListings: onlyBookableListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const SingleBlogPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(SingleBlogPageComponent);

export default SingleBlogPage;
